import { formatNumber, formatCurrency } from '@atc/string-fns';
import { memo, useRef, useContext, useState, useEffect, useCallback, useMemo, startTransition } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDevice } from '@atc/react-device';
import { eventBus } from '@atc/cai-event-bus';
import _capitalize from 'lodash/capitalize';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import _isNumber from 'lodash/isNumber';
import isObject from 'isobject';
import clsx from 'clsx';
import { getConsumerMetadata } from '@atc/consumer-metadata';
import { sendClick } from 'reaxl-analytics';
import { ndsCalcInteraction, ndsPencilingEvents, ndsKBBTradeInCTAClick, ndsPayoffQuoteCTAClick, ndsVerifyCreditCTAClick, myWalletInteractionClick, ndsOfferBreakDownClick, ndsLendingOptionsCTAClick, ndsInitialClick, ndsNavigationClick } from 'reaxl-analytics-handlers';
import { useFeatures } from '@atc/features';
import { Form } from 'reaxl-forms';
import { SelectableTabPanel, SelectableTabs, Text, LoadingSpinner, Button, Glyphicon } from 'reaxl';
import { ratesLabels } from 'axl-config';
import { getIncentives } from 'reaxl-wallet';
import { debugWallet, WALLET_CREATE_ID_REQUEST, UDS_PAYMENT_OBJ_UPDATE, UDS_PAYMENT_CALC_RESPONSE_UPDATE } from 'wallet-manager';
import { getObfuscatedCredit, calculateDownPayment, getCreditTierOptions, calculatePersonalPayment, getPersonalizedPaymentParams, isLoanTermCustom } from '@atc/wallet-payment-fns';
import ConfigContext from '../ConfigContext/ConfigContext.js';
import ndsConstants from './ndsConstants.js';
import useVehiclePaymentDetails from './hooks/useVehiclePaymentDetails.js';
import useExternalDealTypeChange from './hooks/useExternalDealTypeChange.js';
import useExternalIncentivesRequest from './hooks/useExternalIncentivesRequest.js';
import useIncentivesOfferEnabled from './hooks/useIncentivesOfferEnabled.js';
import useLendingOptions from './hooks/useLendingOptions.js';
import useExternalSelectedCriteriaUpdate from './hooks/useExternalSelectedCriteriaUpdate.js';
import useSyncLoanTermWithPaymentsInfo from './hooks/useSyncLoanTermWithPaymentsInfo.js';
import usePencillingDataUpdate from './hooks/usePencillingDataUpdate.js';
import useDealXGData from './hooks/useDealXGData.js';
import useExternalPaymentFetch from './hooks/useExternalPaymentFetch.js';
import useCustomInterestRate from './hooks/useCustomInterestRate.js';
import useUDSAutoFillData from './hooks/useUDSAutoFillData.js';
import useBOACreditApp from './hooks/useBOACreditApp.js';
import CallsToAction from '../CallsToAction/CallsToAction.js';
import TaxesAndFees from '../TaxesAndFees/TaxesAndFees.js';
import Disclaimer from '../Disclaimer/Disclaimer.js';
import buildPayments from './helpers/buildPayments.js';
import buildPaymentInfo from './helpers/buildPaymentInfo.js';
import buildTaxesFeesObject from './helpers/buildTaxesFeesObject.js';
import buildIncentivesParams from './helpers/buildIncentivesParams.js';
import checkTaxesFetchError from './helpers/checkTaxesFetchError.js';
import createPaymentResponse from './helpers/createPaymentResponse.js';
import trackUDSLoadTime from './helpers/trackUDSLoadTime.js';
import applyPaymentCalculationData from './helpers/applyPaymentCalculationData.js';
import buildApiUrl from './helpers/buildApiUrl.js';
import getPaymentErrorState from './helpers/getPaymentErrorState.js';
import getPaymentsOfferObj from './helpers/getPaymentOffer.js';
import getTradeInUpdates from './helpers/getTradeInUpdates.js';
import getUpdatedCreditAndTermChanges from './helpers/getUpdatedCreditAndTermChanges.js';
import getFallbackPaymentObj from './helpers/getFallBackPaymentObj.js';
import { getPenciledListings, addPenciledListing } from './helpers/udsPenciledListings.js';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import CustomDisclaimerText from './components/CustomDisclaimerText.js';
import OfferColumn from './components/OfferColumn.js';
import FinancingCallToAction from './components/FinancingCallToAction.js';
import BOACreditAppForm from './components/BOACreditAppForm.js';
import UDSTitleContainer from './components/UDSTitleContainer.js';
import CashTab from './components/CashTab.js';
import LeaseTab from './components/LeaseTab.js';
import FinanceTab from './components/FinanceTab.js';
import KBBTradeInCTA from './components/KBBTradeInCTA.js';
const {
  defaultRateLabel: DEFAULT_RATE_LABEL,
  tabLabels: TAB_LABELS
} = ndsConstants;

/**
 *  Base Native Deal Structure calculator used on VDP
 */

function NativeDealCalculator(_ref) {
  let {
    additionalContent,
    container,
    // For Syndicated Calc
    creditApp,
    customCallToAction,
    dealerFinancingCTA = {},
    dealsDuck,
    dealXgData,
    enableCreditApp: enableCreditAppCTA = false,
    enableExernalFetchForVehicle,
    env = 'staging',
    // staging or prod
    initialDealType = 'finance',
    listingId,
    listingIncentives = {},
    listingPrice: fallbackListingPrice,
    listingVin,
    onEmailCTAClick,
    onFetchPersonalizedPaymentDetails,
    onFinancingCTAClick,
    onSubmit = () => {},
    ownerCreditAppDeal,
    paymentsDuck,
    personalPaymentProps = {},
    saveDealXgFetcher,
    showNdsWithDeal,
    supportedDealTypes = ['finance', 'cash'],
    userDuck,
    vehiclePaymentDetails = {},
    priceLabel = 'MSRP',
    priceDescription = '',
    ...rest
  } = _ref;
  const {
    accelerate: [enableAccelerate],
    nds_calc: [, {
      trade_in_link: enableTradeIn,
      show_custom_disclaimer_text: showCustomDisclaimerText
    }] = [],
    vdp_new_price_section: [enableNewPriceSectionOnVdp] = []
  } = useFeatures(['accelerate', 'nds_calc', 'vdp_new_price_section']);
  const dispatch = useDispatch();
  const device = useDevice();
  const isXs = device?.is?.xs ?? false;

  // Redux action dispatchers
  const updateIsLoading = data => dispatch(paymentsDuck.creators.updateIsLoading(data));
  const updateMyWallet = data => dispatch(paymentsDuck.creators.updateMyWallet(data));
  const updateMyWalletInteractions = data => dispatch(paymentsDuck.creators.updateMyWalletInteractions(data));
  const updatePaymentsInfo = data => dispatch(paymentsDuck.creators.updatePaymentsInfo(data));
  const updatePenciledLatestPageId = data => dispatch(paymentsDuck.creators.updatePenciledLatestPageId(data));
  const updatePenciledVdpListings = data => dispatch(paymentsDuck.creators.updatePenciledVdpListings(data));
  const updatePencilingPayments = data => dispatch(paymentsDuck.creators.updatePencilingPayments(data));
  const updatePaymentPencils = data => dispatch(paymentsDuck.creators.updatePaymentPencils(data));

  // Redux state selectors
  const inventory = useSelector(state => state?.inventory) || {};
  const brand = useSelector(state => state?.brand) || 'atc';
  const payments = useSelector(state => state?.payments) || {};
  const owners = useSelector(state => state?.owners) || {};
  const user = useSelector(state => state?.user) || {};
  const isLoadingFromRedux = useSelector(state => paymentsDuck.selectors.getIsLoading(state));
  const paymentsInfo = useSelector(state => paymentsDuck?.selectors?.getPaymentsInfo(state));
  const creditVerified = useSelector(paymentsDuck?.selectors?.getCreditVerification)?.verified ?? false;

  // Pick the right owner object
  const {
    owner: inventoryOwner,
    type = 'used',
    ...restInventory
  } = inventory[listingId] || {};
  const isPenciledVdpListing = useSelector(state => paymentsDuck.selectors.isPenciledVdpListing(state, listingId)) || false;

  /**
   * Persist the loading state to prevent payment errors when `isLoading` is `undefined` during re-renders.
   * This can occur because `isLoading` is managed in `paymentsDuck` for Wallet interoperability.
   */
  const lastValidLoadingState = useRef(true);
  const userPaymentUpdatePending = useRef(false);

  // Keep track of the current vehicle payment details to compare on updates
  const currentVehiclePaymentDetails = useRef(vehiclePaymentDetails);
  // Keep track if UDS initial click tagging has been fired
  const hasFiredUDSInitialClickTagging = useRef(false);

  // Store the loading state if it's valid
  if (isLoadingFromRedux !== undefined) {
    lastValidLoadingState.current = isLoadingFromRedux;
  }
  const isLoading = lastValidLoadingState.current; // Always use the last valid loading state

  /**
   * Extract needed config values from the context.
   */
  const {
    msrp: fallbackMsrp,
    dealerId = null,
    // mmd ID
    dealerZip = null,
    defaultFinanceTerms: customDefaultFinanceTerm = null,
    digitalRetailingType,
    disableIncentives = false,
    disableTrade = false,
    enableBOACreditAppForm = false,
    enableCreditApp: enableCreditAppLink = false,
    enableCreditVerification = false,
    enableExternalPaymentFetch,
    enablePayoffQuote = false,
    enableTaxesAndFees = false,
    enableWalletByDefault = false,
    hideSendOfferCTA = false,
    includeTaxesAndFees: applyTaxesAndFees = false,
    interestRates: nationalRates = {},
    isSingleColumn,
    digitalRetailCTALabel,
    listing: listingFromConfig = {},
    partnerId,
    paymentCalculationFetcher = {},
    paymentCalculationUrl,
    paymentIncentivesFetcher = {},
    selectedCriteria = {},
    shopperZip = null,
    showLenderName,
    showLendingOptions = true,
    showNationalAvg = true,
    showWalletSyncBanner = false,
    walletZip = null,
    udsInitializationTime = {},
    ecommerce
  } = useContext(ConfigContext) ?? {};
  const isPrivateSeller = digitalRetailingType === 'psx';
  const owner = {
    ...(isObject(inventoryOwner) && inventoryOwner || owners[inventoryOwner] || {}),
    ...listingFromConfig?.ownerInfo
  };
  const showNewPriceSection = enableNewPriceSectionOnVdp && !isPrivateSeller;
  const creditAppDeals = useSelector(state => paymentsDuck?.selectors?.getCreditAppDealsByOwnerId?.(state, owner?.id)) || [];
  const {
    selectedLoanTerm: loanTerm,
    selectedCreditType: creditTypeValue,
    leaseDownPayment: defaultLeaseDownPayment,
    financeDownPayment: downPaymentValue,
    financeDownPaymentPercentage: downPaymentPercentValue = null,
    interestRate,
    selectedLeaseTerm: leaseTerm,
    selectedLeaseMileage: leaseMileageRange,
    leaseMileageOptions,
    leaseTermOptions,
    tradeInValue = 0,
    amountOwed = 0,
    creditLowScore
  } = selectedCriteria; // criteria selected outside of wallet

  const {
    dealType,
    creditType,
    tradeIn = {},
    interactions: walletInteractions = {},
    walletId,
    loanTerm: savedLoanTerm = 72
  } = paymentsInfo;
  const {
    hasUpdatedCreditOrTerm = false,
    hasRequestedTaxesFees = false,
    paymentEmphasis = false,
    hasUpdatedNDSCalc = false,
    isNationalRates = false
  } = walletInteractions;

  // local variables
  const listingPaymentDetails = payments?.listingPayments?.[listingId] || {};
  const pencilingPaymentDetails = payments?.penciling?.pencilingPayments?.[listingId] || null;
  const userLocationZip = user?.location?.zip;
  const zipCodeValue = walletZip || userLocationZip || shopperZip || dealerZip;
  const zip = zipCodeValue ? zipCodeValue.toString() : null;
  const listingType = type.toLowerCase();
  const vehicleCondition = listingType === 'certified' ? 'used' : listingType;
  const listing = {
    ...restInventory,
    type,
    owner
  };
  const vin = listingVin || listing?.vin || '';
  const {
    ico: [icoTradeIn] = [],
    source = ''
  } = tradeIn;
  const {
    clickType,
    hasSpecialOffer,
    make = '',
    ownerName = ''
  } = listing;
  const customRate = paymentsInfo?.interestRate?.custom ?? null;
  /**
   *  'enableCreditAppCTA' is controlled by brand config in FYC and will be true for Accelerate and SACA credit app's
   *  'enableCreditAppLink' is controlled by tenant settings in syndication and will be true for Accelerate tenant.
   */
  const enableCreditApp = enableCreditAppCTA || enableCreditAppLink;
  /**
   * 'estimatedPayment' is controlled by DDC's payment payload and is used for UDS Ecomm tenant.
   * */
  const isPennyPerfectPayment = vehiclePaymentDetails.estimatedPayment === false;
  const isLeaseEnabled = supportedDealTypes.includes('lease');
  const isCashEnabled = supportedDealTypes.includes('cash');
  const isFinanceEnabled = supportedDealTypes.includes('finance');

  // Local state management
  const [showLendingOptionsModal, setShowLendingOptionsModal] = useState(false);
  const [showOfferBreakdownModal, setShowOfferBreakdownModal] = useState(false);
  const [isFetchingIncentives, setIsFetchingIncentives] = useState(false);
  const [hasIncentivesError, setHasIncentivesError] = useState(false);
  const [appliedIncentiveIds, setAppliedIncentiveIds] = useState({
    finance: [],
    lease: [],
    cash: []
  });
  const [lendingOptions, setLendingOptions] = useState([]);
  const [isLoadingTaxes, setIsLoadingTaxes] = useState(false);

  // Set up tab-based pricing, initialized to an empty object until Payment Services responses are available
  const [cashPricingDetails, setCashPricingDetails] = useState({});
  const [financePricingDetails, setFinancePricingDetails] = useState({});
  const [leasePricingDetails, setLeasePricingDetails] = useState({});

  // set initial state local values
  const [icoTradeInVehicle, setIcoTradeVehicle] = useState(icoTradeIn);
  const [localTradeInValue, setLocalTradeInValue] = useState(tradeInValue);
  const [localAmountOwed, setLocalAmountOwed] = useState(amountOwed);
  const [offerPrice, setOfferPrice] = useState();
  const [showOfferPrice, setShowOfferPrice] = useState(false);
  const [isNDSPenciled, setIsNDSPenciled] = useState(false);
  const [isAutoFilled, setIsAutoFilled] = useState(false);
  const [pencilingEventData, setPencilingEventData] = useState({});
  const selectedInterestRate = typeof interestRate?.[vehicleCondition] === 'number' && interestRate?.[vehicleCondition] >= 0 ? interestRate?.[vehicleCondition] : 4.5;
  const [activeTab, setActiveTab] = useState();
  const [selectedLoanTerm, setSelectedLoanTerm] = useState(loanTerm);
  const [localInterestRate, setLocalInterestRate] = useState(selectedInterestRate);
  const [financeDownPayment, setFinanceDownPayment] = useState(downPaymentValue);
  const [financeDownPaymentPercentageValue, setFinanceDownPaymentPercentageValue] = useState(downPaymentPercentValue);
  const [selectedCreditTier, setSelectedCreditTier] = useState(creditTypeValue);
  const [selectedLeaseTerm, setSelectedLeaseTerm] = useState(leaseTerm);
  const [selectedMileage, setSelectedMileage] = useState(leaseMileageRange);
  const [useCustomRate, setUseCustomRate] = useState(typeof customRate === 'number');
  const [financeOffersObj, setFinanceOffersObj] = useState({});
  const [leaseOffersObj, setLeaseOffersObj] = useState({});
  const [cashOffersObj, setCashOffersObj] = useState({});
  const [hasTaxesFeesModalOpen, setHasTaxesFeesModalOpen] = useState(false);
  const [includeTaxesAndFees, setIncludeTaxesAndFees] = useState(hasRequestedTaxesFees || applyTaxesAndFees);
  const [taxesAndFeesObj, setTaxesAndFeesObj] = useState({});
  const [hasTaxesFetchError, setHasTaxesFetchError] = useState(false);
  const [paymentCalculationDetails, setPaymentCalculationDetails] = useState(pencilingPaymentDetails);
  const [previousCompletedInput, setPreviousCompletedInput] = useState('');
  const [isSupportingLinkClicked, setIsSupportingLinkClicked] = useState(false);
  const [availableIncentives, setAvailableIncentives] = useState([]);
  const obfuscatedCredit = getObfuscatedCredit(selectedCreditTier);
  const {
    paymentsEnabled = true
  } = personalPaymentProps;

  /*
   * Custom hook to ensure Wallet payment term is updated when it changes in the UI.
   */
  useSyncLoanTermWithPaymentsInfo(selectedLoanTerm, selectedCreditTier, paymentsInfo, updatePaymentsInfo);

  /*
   * Handle deal type changes on page load, when updated by the SDP and other initial uds onload logic.
   */
  useEffect(() => {
    if (!activeTab) {
      // calls on page load or VDP TO VDP transition
      setActiveTab(initialDealType);
      const paymentsInfoPayload = {
        dealType: initialDealType,
        zip,
        ...(enableWalletByDefault && enableExternalPaymentFetch && {
          // Apply ecomm defaults to wallet to make sure both are in sync
          ...buildPayments(selectedCriteria, obfuscatedCredit),
          // We have to specifically set the paymentEmphasis to true for CoxAuto tenant due to a timing issue.
          interactions: {
            paymentEmphasis: true,
            hasOpenedWallet: true
          }
        })
      };
      updatePaymentsInfo(paymentsInfoPayload);
      updateMyWalletInteractions({
        hasWalletInteractionWithinVDP: false,
        // Enable wallet by default globally
        ...(enableWalletByDefault && {
          paymentEmphasis: true
        })
      });
      trackUDSLoadTime(ecommerce, udsInitializationTime, vin, listingId);
      return;
    }
    if (dealType && dealType !== activeTab) {
      setActiveTab(dealType);
      setPencilingEventData(prevData => ({
        ...prevData,
        dealType
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealType]);
  const {
    originalSalePrice,
    salePrice,
    msrp,
    salePriceBasedOnMSRP = false
  } = {
    cash: !_isEmpty(cashPricingDetails) && cashPricingDetails,
    lease: !_isEmpty(leasePricingDetails) && leasePricingDetails,
    finance: !_isEmpty(financePricingDetails) && financePricingDetails
  }[activeTab] || {};
  const isDRAccelerate = enableAccelerate && digitalRetailingType === 'accelerate';
  const ndsPencilingFeature = isDRAccelerate ? 'dr3' : 'MyWallet';
  const isLeaseDeal = activeTab === 'lease';
  const isFinanceDeal = activeTab === 'finance';
  const icoTradeInOffer = icoTradeInVehicle?.instantCashOffer ?? false;

  // Price determination logic
  let price;
  if (['lease', 'cash'].includes(activeTab)) {
    price = salePriceBasedOnMSRP ? msrp : originalSalePrice;
  }
  if (!price) {
    price = originalSalePrice || salePrice || msrp || fallbackListingPrice;
  }
  const [localPrice, setLocalPrice] = useState(price);

  // Fallback down payment calculation based on local price.
  const walletDownPaymentFallBack = calculateDownPayment(localPrice, {
    downPayment: financeDownPaymentPercentageValue === null ? financeDownPayment : null,
    downPaymentPercent: financeDownPaymentPercentageValue
  });
  const [leaseDownPayment, setLeaseDownPayment] = useState(paymentEmphasis ? walletDownPaymentFallBack : defaultLeaseDownPayment);
  const leaseDownPaymentFallBack = calculateDownPayment(localPrice, {
    downPayment: leaseDownPayment || null,
    downPaymentPercent: 0.1
  });
  const handleNDSClick = useCallback(async (event, inputObj) => {
    if (!isNDSPenciled && !getPenciledListings().includes(listingId)) {
      const {
        userModified,
        elementText,
        targetTab,
        inputName,
        inputValue
      } = inputObj;
      setIsNDSPenciled(true);
      const buildConsumerMetadata = async walletIdValue => {
        const consumerMetadata = await getConsumerMetadata();
        consumerMetadata.walletId = walletIdValue;
        return consumerMetadata;
      };
      sendClick(ndsCalcInteraction, event, {
        listingId,
        feature: ndsPencilingFeature,
        consumerMetadata: await buildConsumerMetadata(walletId),
        walletData: {
          paymentsInfo,
          userModified,
          price,
          offerPrice
        },
        elementText,
        paymentsDuck,
        activeTab: targetTab ?? activeTab,
        // If this is a tab switch, we pass the target tab instead
        leasePayment: leaseOffersObj?.payment,
        financePayment: financeOffersObj?.payment,
        inputName,
        inputValue
      });
      addPenciledListing(listingId);
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [isNDSPenciled, listingId, ndsPencilingFeature, sendClick, walletId, price, offerPrice, activeTab]);

  /**
   * Handles tab selection change from user click interaction. Sets the new active tab, updates wallet payment info,
   * and sends analytics events.
   *
   * @param {('finance'|'lease'|'cash')} key - The key of the tab to select
   * @param {SyntheticEvent} event - The event that triggered the tab selection
   */
  const onTabSelect = function (event) {
    let key = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'finance';
    setActiveTab(key);
    const userModified = 'DEAL_TAB_CHANGED';
    updatePaymentsInfo({
      ...paymentsInfo,
      dealType: key,
      /* When wallet is not enabled and Tabs are pencilled first time override wallet with UDS selections */
      ...(!paymentEmphasis && {
        ...buildPayments(selectedCriteria, obfuscatedCredit),
        interactions: {
          ...paymentsInfo.interactions,
          paymentEmphasis: true
        }
      })
    });
    sendClick(ndsNavigationClick, event, {
      cmp: `${key}-tab`,
      eventResult: `${key}-tab`,
      listingId,
      walletData: {
        paymentsInfo,
        userModified,
        price,
        offerPrice
      },
      paymentsDuck,
      key
    });
    handleNDSClick(event, {
      userModified,
      elementText: _capitalize(key),
      targetTab: key
    });
  };
  const handleFallbackPayment = fallbackPayment => updatePaymentPencils({
    fallbackPayment
  });
  const handleCreateWalletId = useCallback(() => {
    if (!walletId) {
      debugWallet('UDS', 'create wallet ID request from UDS');
      eventBus.emit(WALLET_CREATE_ID_REQUEST);
    }
  }, [walletId]);
  const handleNDSPencilling = useCallback(async (event, eventData) => {
    handleCreateWalletId();
    const {
      cmp,
      eventResult,
      eventName,
      feature,
      userModified = '',
      inputName,
      inputValue
    } = eventData;
    sendClick(ndsPencilingEvents, event, {
      listingId,
      cmp,
      eventResult,
      eventName,
      feature,
      activeTab,
      walletData: {
        paymentsInfo,
        userModified,
        price,
        offerPrice
      },
      inputName,
      inputValue,
      paymentsDuck
    });
  }, [handleCreateWalletId, listingId, activeTab, paymentsInfo, price, offerPrice, paymentsDuck]);
  const handlePaymentsFallback = function () {
    let showNationalRates = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
    setLeaseOffersObj({});
    setFinanceOffersObj({});
    updateIsLoading({
      isLoading: false
    });
    updateMyWalletInteractions({
      hasLeaseError: true,
      isNationalRates: showNationalRates
    });
  };

  // destructure lease object when present
  const {
    payment: leasePayment,
    downPayment: leaseDownPaymentValue,
    capCost: {
      adjustedCapCost,
      capitalizedTaxesAndFees
    } = {},
    dueInAdvance: {
      firstPayment,
      cashDueFromCustomer,
      diaTaxes,
      diaDealerFees,
      diaGovFees,
      diaLenderFees
    } = {}
  } = leaseOffersObj;

  // Calculate total equity
  let totalEquity = 0;
  if (!disableTrade) {
    totalEquity = (icoTradeInOffer || localTradeInValue) - localAmountOwed;
  }
  let dueAtSigningValue;
  if (isLeaseEnabled && isLeaseDeal) {
    dueAtSigningValue = typeof leaseDownPayment === 'number' && leaseDownPayment >= 0 ? leaseDownPayment : leaseDownPaymentValue;
  }
  const udsLocalStateValues = {
    leaseDownPayment,
    localPrice,
    selectedMileage,
    selectedLeaseTerm,
    selectedCreditTier,
    obfuscatedCredit,
    financeDownPaymentPercentageValue,
    financeDownPayment,
    localInterestRate,
    selectedLoanTerm,
    activeTab,
    initialDealType,
    offerPrice,
    localTradeInValue,
    localAmountOwed,
    financeOffersObj,
    leaseOffersObj,
    cashOffersObj,
    isLeaseEnabled,
    isCashEnabled,
    isFinanceEnabled,
    useCustomRate,
    customRate,
    listingId,
    vehicleCondition,
    totalEquity,
    dueAtSigningValue
  };

  // Only used for firing UDS pencilling data of limited set mostly used by PSX web components.
  usePencillingDataUpdate(pencilingEventData, udsLocalStateValues);

  // Specifically used for firing UDS pencilling data in syndicated version of esntial VDP integrated with external payments handled via event communication.
  useExternalPaymentFetch(onFetchPersonalizedPaymentDetails, zip, enableExernalFetchForVehicle, isNDSPenciled, updateIsLoading, udsLocalStateValues);

  // Calculate total tax
  let totalTax = 0;
  if (enableTaxesAndFees) {
    const offersMap = {
      finance: financeOffersObj,
      lease: leaseOffersObj,
      cash: cashOffersObj
    };
    totalTax = Math.floor(offersMap[activeTab]?.taxesFees?.totalTaxesFees || 0);
  }

  // Calculate cash values
  const totalCash = cashOffersObj?.pricingDetails?.salePrice || cashOffersObj?.pricingDetails?.msrp || localPrice;
  const totalTaxOnCash = enableTaxesAndFees && cashOffersObj?.taxesFees?.totalTaxesFees ? cashOffersObj?.taxesFees?.totalTaxesFees : 0;
  const totalCashOut = totalCash - totalEquity + totalTaxOnCash;

  // Calculate financing
  let totalFinancing = localPrice - totalEquity - financeDownPayment + totalTax;
  if (isLeaseEnabled && isLeaseDeal) {
    totalFinancing = adjustedCapCost || 0;
  }
  const formattedTotalPrice = formatNumber({
    value: localPrice
  });
  const formattedTotalTax = formatNumber({
    value: totalTax
  });
  const isFinancingUnavailable = totalFinancing < 0;
  const {
    paymentErrorState,
    isFinanceErrorState,
    isLeaseErrorState
  } = getPaymentErrorState({
    isFinancingUnavailable,
    financeOffersObj,
    leaseOffersObj,
    showNationalAvg,
    isFinanceDeal,
    isLeaseDeal,
    isLoading
  });

  // Used only to prepopulate UDS with existing deal where receipt is hidden which possible only for Non DR dealers.
  useDealXGData(showNdsWithDeal, dealXgData, paymentsInfo, updatePaymentsInfo, udsLocalStateValues);
  const {
    formData: boaFormData = {},
    formRef: boaFormRef = undefined
  } = useBOACreditApp(dealerId, listing, udsLocalStateValues, ecommerce) || {};

  // Used to update all local state
  useEffect(() => {
    setLocalAmountOwed(amountOwed);
    setFinanceDownPayment(downPaymentValue ?? walletDownPaymentFallBack);
    setFinanceDownPaymentPercentageValue(downPaymentPercentValue);
    setLocalTradeInValue(tradeInValue);
    setSelectedCreditTier(creditTypeValue);
    setSelectedLoanTerm(loanTerm);
    setSelectedLeaseTerm(leaseTerm);
    setSelectedMileage(leaseMileageRange);
    if (icoTradeIn) {
      // Checking for icoTradeIn so that ico checkbox is visible all time even on user entered tradeIn.
      setIcoTradeVehicle(icoTradeIn);
    }
    if (downPaymentValue && isLeaseEnabled) {
      // Apply downPaymentValue to leaseDownPayment
      setLeaseDownPayment(downPaymentValue);
    }
    if (!showOfferPrice) {
      setLocalPrice(price);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amountOwed, downPaymentValue, tradeInValue, creditTypeValue, loanTerm, leaseTerm, leaseMileageRange, icoTradeIn, downPaymentPercentValue, isLeaseEnabled, price, showOfferPrice]);
  const {
    fallBackFinanceObj,
    fallBackLeaseObj,
    fallBackCashObj
  } = getFallbackPaymentObj(isFinanceErrorState, walletDownPaymentFallBack, totalEquity, totalCashOut, udsLocalStateValues);

  /**
   * Updates taxes and fees data with actual offer data
   * @param offerValues
   */
  const updateTaxesAndFeesWithOfferData = offerValues => {
    const taxesFeesData = buildTaxesFeesObject(vehiclePaymentDetails, enableExternalPaymentFetch, offerValues);
    setTaxesAndFeesObj(taxesFeesData);
    updateMyWalletInteractions({
      isNationalRates: false
    });
  };

  /**
   * Updates wallet interactions to indicate national rates are being used
   */
  const updateToNationalRatesMode = () => {
    if (showNationalAvg) {
      updateMyWalletInteractions({
        isNationalRates: true
      });
    }
  };

  /**
   * Stores incentive IDs for enabled offer types
   * @param offerValues
   */
  const storeIncentiveIds = offerValues => {
    if (digitalRetailingType !== 'accelerate') {
      return;
    }
    const {
      financeOfferValues,
      leaseOfferValues,
      cashOfferValues
    } = offerValues;
    const offerTypes = [{
      offerType: 'finance',
      values: financeOfferValues,
      enabled: isFinanceEnabled
    }, {
      offerType: 'lease',
      values: leaseOfferValues,
      enabled: isLeaseEnabled
    }, {
      offerType: 'cash',
      values: cashOfferValues,
      enabled: isCashEnabled
    }];
    offerTypes.forEach(_ref2 => {
      let {
        offerType,
        values,
        enabled
      } = _ref2;
      if (enabled && !_isEmpty(values) && values.incentiveIds?.length) {
        setAppliedIncentiveIds(prev => ({
          ...prev,
          [offerType]: values.incentiveIds
        }));
      }
    });
  };

  /**
   * Handles the case when payment calculation data should not be applied
   * @param {Object} localFinanceObj - Local finance object
   * @param {Object} localLeaseObj - Local lease object
   * @param {Object} localCashObj - Local cash object
   * @returns {Object} Existing or fallback offer objects
   */
  const handleNonAppliedPaymentData = (localFinanceObj, localLeaseObj, localCashObj) => {
    const updatedFinanceObj = !_isEmpty(financeOffersObj) ? {
      ...financeOffersObj
    } : localFinanceObj;
    const updatedLeaseObj = !_isEmpty(leaseOffersObj) ? {
      ...leaseOffersObj
    } : localLeaseObj;
    const updatedCashObj = !_isEmpty(cashOffersObj) ? {
      ...cashOffersObj
    } : localCashObj;
    return {
      updatedFinanceObj,
      updatedLeaseObj,
      updatedCashObj
    };
  };

  // function to emit UDS event with updated payment objects to vdp
  const updateUDSPaymentObject = useCallback(function () {
    let financeObj = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    let leaseObj = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    let cashObj = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    // check if the objects are empty and use fallbacks if they are
    const updatedFinanceObj = Object.keys(financeObj).length > 0 ? financeObj : fallBackFinanceObj;
    const updatedLeaseObj = Object.keys(leaseObj).length > 0 ? leaseObj : fallBackLeaseObj;
    const updatedCashObj = Object.keys(cashObj).length > 0 ? cashObj : fallBackCashObj;
    eventBus.emit(UDS_PAYMENT_OBJ_UPDATE, [{
      ...updatedFinanceObj
    }, {
      ...updatedLeaseObj
    }, {
      ...updatedCashObj
    }]);
  }, [fallBackFinanceObj, fallBackLeaseObj, fallBackCashObj]);

  // apply payments data from payment calculation service to calculator.
  useEffect(() => {
    let localFinanceObj = {
      ...fallBackFinanceObj
    };
    let localLeaseObj = {
      ...fallBackLeaseObj
    };
    let localCashObj = {
      ...fallBackCashObj
    };
    if (!_isEmpty(paymentCalculationDetails) && selectedCreditTier && (selectedLoanTerm || selectedLeaseTerm)) {
      // only when finance options are selected, we will persist lender by name otherwise default to lowest when paymentCalculationDetails is fetched
      const offerValues = getPaymentsOfferObj(paymentCalculationDetails, udsLocalStateValues);
      const isNationalRatesValue = !_isEmpty(paymentCalculationDetails?.rates?.[obfuscatedCredit]?.nationalOffers?.[selectedLoanTerm]?.[0]);

      // Apply taxes and fees data or set national rates mode based on conditions
      if (hasTaxesFeesModalOpen || showOfferBreakdownModal || !isNationalRatesValue) {
        updateTaxesAndFeesWithOfferData(offerValues);
      } else if (isNationalRatesValue) {
        updateToNationalRatesMode();
      }
      storeIncentiveIds(offerValues);
      const offerResults = paymentCalculationDetails.apply ? applyPaymentCalculationData(offerValues, {
        localFinanceObj,
        localLeaseObj,
        localCashObj
      }, udsLocalStateValues, {
        setFinanceOffersObj,
        setLeaseOffersObj,
        setCashOffersObj
      }, paymentsDuck, dispatch) : handleNonAppliedPaymentData(localFinanceObj, localLeaseObj, localCashObj);
      localFinanceObj = offerResults.updatedFinanceObj;
      localLeaseObj = offerResults.updatedLeaseObj;
      localCashObj = offerResults.updatedCashObj;
    }

    // fallBackFinanceObject is updated with customRate if customRate is selected
    const sendCustomRateFinanceObj = useCustomRate && customRate;
    updateUDSPaymentObject(sendCustomRateFinanceObj ? fallBackFinanceObj : localFinanceObj, localLeaseObj, localCashObj);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentCalculationDetails]);

  /**
   * Processes successful payment calculation response
   * @param {Object} detailRates - Response from payment calculation API
   * @param {boolean} applyResults - Whether to apply the results
   */
  const processSuccessfulPaymentResponse = (detailRates, applyResults) => {
    if (checkTaxesFetchError(detailRates.rates, activeTab, obfuscatedCredit)) {
      setHasTaxesFetchError(true);
    }
    const calculationDetails = {
      ...detailRates,
      apply: applyResults
    };
    setPaymentCalculationDetails(calculationDetails);
    if (listingId && updatePencilingPayments) {
      updatePencilingPayments({
        [listingId]: calculationDetails
      });
    }
    updateIsLoading({
      isLoading: false
    });
  };

  /**
   * Processes failed payment calculation response
   * @param {Object} detailRates - Response from payment calculation API
   */
  const processFailedPaymentResponse = detailRates => {
    // eslint-disable-next-line no-console
    console.error('Error in fetching Payment Calc response:', detailRates?.error?.message);
    handlePaymentsFallback(showNationalAvg);
    updateUDSPaymentObject();
    setHasTaxesFetchError(true);
  };

  /**
   * Checks if payment calculation conditions are met
   * @returns {boolean} Whether conditions are met for API call
   */
  const shouldFetchPaymentCalculation = userZip => Object.keys(paymentCalculationFetcher).length > 0 && paymentCalculationUrl && userZip;

  /**
   * Checks if payment calculation response indicates an error
   * @param {Object} detailRates - Response from payment calculation API
   * @returns {boolean} Whether response indicates an error
   */
  const isErrorPaymentResponse = detailRates => Object.keys(detailRates).length === 0 || detailRates?.error;

  /**
   * Sends payment response to external systems
   * @param {Object} paymentResponse - Payment response object
   */
  const sendPaymentResponse = paymentResponse => {
    debugWallet('UDS', 'PaymentCalcResponse:Update', paymentResponse);
    try {
      // Send updated payment info to external tenant and Fire Event even if there is an error from payment calc.
      eventBus.emit(UDS_PAYMENT_CALC_RESPONSE_UPDATE, paymentResponse);
    } catch (error) {
      updateIsLoading({
        isLoading: false
      }); // Ensure loading state is reset even if external code fails
      // eslint-disable-next-line no-console
      console.error('Error sending payment info to tenant:', error);
    }
  };

  // fetch payments data from payment calculation service.
  const fetchPersonalizedPayment = async function () {
    let withTaxesAndFees = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    let applyResults = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    let userZip = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : zip;
    let detailRates = {};
    if (shouldFetchPaymentCalculation(userZip)) {
      updateIsLoading({
        isLoading: true
      });
      const _paymentsInfo = buildPaymentInfo({
        withTaxesAndFees,
        walletDownPaymentFallBack,
        leaseDownPaymentFallBack,
        isLeaseEnabled,
        udsLocalStateValues,
        paymentEmphasis,
        hasUpdatedNDSCalc,
        disableTrade,
        enableTaxesAndFees,
        icoTradeInOffer,
        downPaymentValue // Should be picked from payment Info instead of local value
      });
      const personalizedPaymentParams = getPersonalizedPaymentParams({
        paymentsInfo: _paymentsInfo,
        userZip,
        offerPrice
      });
      setHasTaxesFetchError(false);
      const apiUrl = buildApiUrl(personalizedPaymentParams, creditTypeValue, creditLowScore, paymentCalculationUrl);
      detailRates = (await paymentCalculationFetcher.fetch(apiUrl)) || {};
      if (detailRates?.rates) {
        processSuccessfulPaymentResponse(detailRates, applyResults);
      } else if (isErrorPaymentResponse(detailRates)) {
        processFailedPaymentResponse(detailRates);
      }
    } else {
      handlePaymentsFallback(showNationalAvg);
    }

    // Handle external payment events
    const {
      financeOfferValues,
      leaseOfferValues
    } = getPaymentsOfferObj(detailRates, udsLocalStateValues);
    const {
      paymentErrorState: paymentError,
      isFinanceErrorState: financeError,
      isLeaseErrorState: leaseError
    } = getPaymentErrorState({
      isFinancingUnavailable,
      financeOffersObj: financeOfferValues,
      leaseOffersObj: leaseOfferValues,
      showNationalAvg,
      isFinanceDeal: isFinanceDeal || initialDealType === 'finance',
      isLeaseDeal: isLeaseDeal || initialDealType === 'lease',
      isLoading: false // doesn't have the updated state value yet, manually sending it
    });
    const paymentResponse = createPaymentResponse({
      detailRates,
      supportedDealTypes,
      isLeaseEnabled,
      paymentErrorState: paymentError,
      isFinanceErrorState: financeError,
      isLeaseErrorState: leaseError,
      udsLocalStateValues
    });
    sendPaymentResponse(paymentResponse);
  };
  const consumePersonalizedPayment = useCallback(updatePayment => {
    if (updatePayment && enableExternalPaymentFetch) {
      setPaymentCalculationDetails({
        ...vehiclePaymentDetails,
        apply: !hasTaxesFeesModalOpen
      });
      currentVehiclePaymentDetails.current = vehiclePaymentDetails;
      updateIsLoading({
        isLoading: false
      });
    } else if (!updatePayment && isLoading && enableExternalPaymentFetch && !_isEqual(currentVehiclePaymentDetails.current, vehiclePaymentDetails)) {
      handlePaymentsFallback(showNationalAvg);
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [vehiclePaymentDetails]);
  const extractVin = url => {
    const match = url.match(/vin=([A-Za-z0-9]+)/);
    if (match) {
      return match[1];
    }
    return null;
  };
  const _financeDownPaymentPercent = financeDownPayment >= 0 ? null : financeDownPaymentPercentageValue;
  const _leaseDownPaymentDep = financeDownPayment >= 0 ? null : leaseDownPayment;
  const _downPaymentDep = financeDownPaymentPercentageValue !== null ? financeDownPaymentPercentageValue : financeDownPayment;
  useEffect(() => {
    if (paymentsEnabled && !enableExternalPaymentFetch && paymentCalculationUrl) {
      // vin comparison to prevent a race condition in which a duplicate Payment Calc call is made
      // with a previous VDP's paymentCalculationUrl upon VDP to VDP navigation due to a delayed config update
      const vinFromUrl = extractVin(paymentCalculationUrl);
      if (vinFromUrl === vin) {
        fetchPersonalizedPayment(includeTaxesAndFees);
      } else {
        // eslint-disable-next-line no-console
        console.error('VIN mismatch between listing and paymentCalculationUrl');
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
  // The below dependency array determines when to make a call to payments if any of the value changes
  customRate, _financeDownPaymentPercent,
  // Down payment percentage has dependency with Finance Down payment
  _leaseDownPaymentDep,
  // Lease Down payment has dependency with Finance Down payment
  _downPaymentDep,
  // Finance Down payment has dependency with Down payment percentage
  icoTradeInOffer, isLeaseEnabled, listingId, localAmountOwed, localTradeInValue, offerPrice, paymentCalculationUrl, paymentsEnabled, selectedCreditTier, selectedLeaseTerm, selectedLoanTerm, selectedMileage, useCustomRate, vehicleCondition]); // If any changes to above array then we need test the api calls before and after wallet in enabled were it has to be only one api call.

  useVehiclePaymentDetails(vehiclePaymentDetails, consumePersonalizedPayment, obfuscatedCredit, selectedLoanTerm, selectedLeaseTerm, vehicleCondition, showLenderName, activeTab, hasTaxesFeesModalOpen, showOfferBreakdownModal);

  /**
   * Updates component state when external payment calculation results are received. This hook applies server-computed
   * payment data to local state, including pricing details, down payments, and interest rates from the payment
   * calculation service, overriding local calculations with server-provided values when available.
   *
   * @see {@link updateUserPaymentInfo} for initial user-driven payment updates
   */
  useEffect(() => {
    setFinancePricingDetails(financeOffersObj?.pricingDetails ?? {});
    setFinanceDownPayment(financeOffersObj?.downPayment ?? walletDownPaymentFallBack);
    // Interest rate update
    if (typeof financeOffersObj.interestRate === 'number' && financeOffersObj.interestRate >= 0 && typeof customRate !== 'number') {
      setLocalInterestRate(financeOffersObj.interestRate);
      setUseCustomRate(false);
    }

    // Lease
    if (isLeaseEnabled) {
      // No need of uncessary state updates for NO lease
      setLeasePricingDetails(leaseOffersObj?.pricingDetails ?? {});
      setLeaseDownPayment(paymentEmphasis ? financeOffersObj?.downPayment || walletDownPaymentFallBack : leaseOffersObj?.downPayment || leaseDownPaymentFallBack);
    }

    // Cash
    setCashPricingDetails(cashOffersObj?.pricingDetails ?? {});

    // Clear loading only when this update was triggered by a user action; this avoids ending the initial load early
    if (userPaymentUpdatePending.current) {
      updateIsLoading({
        isLoading: false
      });
      userPaymentUpdatePending.current = false;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [financeOffersObj, leaseOffersObj, cashOffersObj, isLeaseEnabled, price]);

  // apply custom interest
  useCustomInterestRate(customRate, setLocalInterestRate, setUseCustomRate);

  // UDS Auto fill effect - to auto fill only once when wallet is opened and there is no user interaction with wallet on VDP.
  useUDSAutoFillData({
    updateMyWalletInteractions,
    isAutoFilled,
    setIsAutoFilled,
    isLoading,
    creditType,
    downPaymentValue,
    dueAtSigningValue,
    paymentsInfo,
    paymentsDuck,
    leasePayment,
    udsLocalStateValues
  });
  useExternalDealTypeChange(supportedDealTypes, setActiveTab, updatePaymentsInfo, paymentsDuck, {
    // analyticsData params
    listingId,
    price,
    offerPrice
  }, handleNDSClick, selectedCriteria, obfuscatedCredit);
  useExternalIncentivesRequest({
    activeTab,
    dealerId,
    partnerId,
    listingType: listing?.type,
    vin,
    zip,
    paymentIncentivesFetcher
  });
  const {
    isIncentivesOfferEnabled,
    incentivesUrlFromListing
  } = useIncentivesOfferEnabled({
    paymentsDuck,
    listingId,
    zip,
    dealerId,
    partnerId,
    listing,
    appliedIncentiveIds,
    activeTab,
    availableIncentives,
    hasSpecialOffer,
    listingType,
    disableIncentives
  });
  const fetchIncentivesDetails = useCallback(async function () {
    let overrideZip = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    setIsFetchingIncentives(true);
    const currentZip = overrideZip || zip;
    const hasIncentivesUrl = !!incentivesUrlFromListing;
    const incentivesParams = buildIncentivesParams({
      hasIncentivesUrl,
      activeTab,
      zip: currentZip,
      listingType: listing?.type,
      vin,
      dealType: activeTab,
      dealerId,
      partnerId
    });
    try {
      const incentives = await getIncentives({
        incentivesFetcher: paymentIncentivesFetcher,
        incentivesUrl: incentivesUrlFromListing ?? new URLSearchParams(incentivesParams).toString(),
        incentivesParams: hasIncentivesUrl ? incentivesParams : {}
      });
      if (incentives?.[0]?.hasError) {
        setHasIncentivesError(true);
      }
      setAvailableIncentives(incentives);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch incentives:', error);
      setHasIncentivesError(true);
      setAvailableIncentives([]);
    } finally {
      setIsFetchingIncentives(false);
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [zip, incentivesUrlFromListing, activeTab, paymentIncentivesFetcher, vin]);
  const incentivesProps = useMemo(() => {
    const appliedIncentives = availableIncentives?.filter(incentive => (appliedIncentiveIds[activeTab] || []).includes(incentive.incentiveId)) || [];
    return {
      hasIncentivesError,
      isFetchingIncentives,
      appliedIncentives
    };
  }, [hasIncentivesError, isFetchingIncentives, availableIncentives, appliedIncentiveIds, activeTab]);
  const creditTierOptions = getCreditTierOptions(nationalRates);

  /**
   * Builds interactions object for payment updates
   * @param {string} key - The parameter key being updated
   * @param {boolean} updatesCreditOrTerm - Whether credit or term was updated
   * @returns {Object} Interactions object
   */
  const buildPaymentInteractions = (key, updatesCreditOrTerm) => ({
    ...paymentsInfo.interactions,
    isNationalRates: false,
    // resetting national rates.
    hasUpdatedNDSCalc: true,
    hasUpdatedCreditOrTerm: hasUpdatedCreditOrTerm || updatesCreditOrTerm,
    paymentEmphasis: true,
    disablePaymentsPopover: true,
    ...(key === 'hasRequestedTaxesFees' && {
      hasRequestedTaxesFees: true
    }),
    fromNds: true
  });

  /**
   * Builds complete payment object for user payment info updates
   * @param {string} key - The parameter key being updated
   * @param {*} value - The new value
   * @param {Object} creditTermData - Processed credit and term data
   * @param {Object} updatedTradeIn - Updated trade-in object
   * @param {string|null} updatedZip - Updated ZIP code
   * @param {boolean} hasCreditVerified - Whether credit has been verified
   * @returns {Object} Complete payment object
   */
  const buildCompletePaymentObject = (key, value, creditTermData, updatedTradeIn, updatedZip, hasCreditVerified) => {
    const {
      isCreditType,
      derivedCreditType,
      updatesCreditOrTerm,
      rateObject,
      fallBackLoanTerm
    } = creditTermData;
    return {
      /* When wallet is not enabled and UDS is pencilled first time override wallet with UDS selections */
      ...(!paymentEmphasis && buildPayments(selectedCriteria, obfuscatedCredit)),
      creditType: derivedCreditType,
      ...(key === 'downPayment' && {
        downPaymentPercent: null
      }),
      // downPaymentPercent null when there is a down payment pencil
      ...(!isCreditType && {
        [key]: value
      }),
      ...(Object.keys(updatedTradeIn).length > 0 && {
        tradeIn: updatedTradeIn
      }),
      // fall back to 72 term if user changes credit tier after loan term selection of 84 which applies only to excellent tier
      ...(Number(selectedLoanTerm) === 84 && isCreditType && value !== 'excellent' && customDefaultFinanceTerm !== 84 && {
        loanTerm: 72
      }),
      fallBackLoanTerm,
      interestRate: !_isEmpty(rateObject) ? rateObject : {
        new: localInterestRate,
        used: localInterestRate
      },
      interactions: buildPaymentInteractions(key, updatesCreditOrTerm),
      ...(updatedZip && {
        zip: updatedZip
      }),
      ...(hasCreditVerified && {
        creditVerification: {
          verified: hasCreditVerified,
          verifiedDate: new Date().getTime()
        }
      })
    };
  };

  /**
   * Handles user-initiated changes to payment calculation parameters and applies national rate logic for credit tiers
   * and loan terms. This function processes user inputs like credit type changes, loan term selections, trade-in
   * values, and down payments, then calculates appropriate interest rates from national rates data and updates the
   * payment state. This function drives the initial calculation logic and user interaction flow within the calculator
   * before external payment offers are fetched and applied.
   *
   * @param {string} key - The payment parameter being updated (e.g., 'creditType', 'loanTerm', 'downPayment')
   * @param {*} value - The new value for the parameter
   * @param {string|null} [updatedZip=null] - Updated ZIP code if location has changed
   * @param {boolean} [hasCreditVerified=false] - Whether credit has been verified for this update
   */
  const updateUserPaymentInfo = useCallback(function (key, value) {
    let updatedZip = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
    let hasCreditVerified = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
    // Mark this as a user-initiated load so the offers effect can clear the loading state when updates apply
    userPaymentUpdatePending.current = true;
    if (key) {
      // Should not update on any CTA Clicks
      updateIsLoading({
        isLoading: true
      });
    }
    const creditTermData = getUpdatedCreditAndTermChanges(key, value, savedLoanTerm, nationalRates, ratesLabels, isLoanTermCustom, udsLocalStateValues);
    const updatedTradeIn = getTradeInUpdates(key, value, tradeIn);
    const paymentObject = buildCompletePaymentObject(key, value, creditTermData, updatedTradeIn, updatedZip, hasCreditVerified);
    const {
      isCreditType,
      isLoanTerm,
      rateObject
    } = creditTermData;
    if ((isCreditType || isLoanTerm) && Object.keys(rateObject).length !== 0) {
      setLocalInterestRate(useCustomRate ? rateObject.custom : rateObject[vehicleCondition]);
    }
    updatePaymentsInfo(paymentObject);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [nationalRates, selectedCreditTier, selectedLoanTerm, useCustomRate, customRate, updatePaymentsInfo, paymentsInfo]);
  const handleSelectChange = event => {
    const {
      target
    } = event;
    let cmp = '';
    let eventResult = '';
    let eventName;
    let userModified = '';
    let elementText = '';
    if (target.name === 'creditTierInput') {
      userModified = 'CREDIT_TIER_CHANGED';
      const splitLabels = nationalRates[target.value].label.split(' (');
      const mappedLabels = [];
      splitLabels.forEach((v, i) => {
        if (typeof v !== 'undefined') {
          if (i === 0) {
            mappedLabels.push(`${v}(`);
          } else {
            mappedLabels.push(v.replaceAll(/\s/g, ''));
          }
        }
      });
      const optionText = mappedLabels.join('');
      updateUserPaymentInfo('creditType', getObfuscatedCredit(target.value));
      cmp = `crd_rtg - ${optionText.toLowerCase()}`;
      eventResult = 'credit-rating';
      elementText = 'Credit Rating';
    } else if (target.name === 'termInput') {
      userModified = 'TERM_LENGTH_CHANGED';
      updateUserPaymentInfo('loanTerm', Number(target.value));
      cmp = `term_lmt-${target.value}`;
      eventResult = 'term';
      elementText = 'Finance Term';
    } else if (target.name === 'mileageInput') {
      userModified = 'MILES_INPUT_CHANGED';
      updateUserPaymentInfo('leaseMileageRange', target.value);
      cmp = `nds_lease-term-${selectedLeaseTerm}-${formatNumber({
        value: target.value
      })}`;
      eventName = 'lease term';
      eventResult = 'miles-per-year';
      elementText = 'Mileage Range';
    } else if (target.name === 'leaseTermInput') {
      userModified = 'LEASE_TERM_LENGTH_CHANGED';
      updateUserPaymentInfo('leaseTerm', target.value);
      cmp = `nsd_lease-term-${target.value}-${formatNumber({
        value: selectedMileage
      })}`;
      eventName = 'lease term';
      eventResult = 'lease';
      elementText = 'Lease Term';
    }
    setPreviousCompletedInput(target.name);
    handleNDSClick(event, {
      userModified,
      elementText,
      inputName: target.name,
      inputValue: target.value
    });
    handleNDSPencilling(event, {
      cmp,
      eventResult,
      eventName,
      feature: ndsPencilingFeature,
      userModified,
      inputName: target.name,
      inputValue: target.value
    });
  };
  const handleInputChange = useCallback(e => {
    e.persist();
    const {
      value,
      name
    } = e.target;
    const sanitizedValue = parseInt(value.replace(/[^\d-]/g, '') || 0, 10);
    handleCreateWalletId();
    const inputMap = {
      tradeInInput: {
        userModified: 'Trade_IN_VALUE_CHANGED',
        elementText: 'Trade-In',
        action: () => updateUserPaymentInfo('tradeInInput', sanitizedValue)
      },
      amountOwedInput: {
        userModified: 'AMOUNT_OWED_CHANGED',
        elementText: 'Amount Owed',
        action: () => updateUserPaymentInfo('amountOwedInput', sanitizedValue)
      },
      downPaymentInput: {
        userModified: 'DOWN_PAYMENT_CHANGED',
        elementText: 'Down Payment',
        action: () => updateUserPaymentInfo('downPayment', sanitizedValue)
      },
      dueAtSigning: {
        userModified: 'DUE_AT_SIGNING_CHANGED',
        elementText: 'Due At Signing',
        action: () => updateUserPaymentInfo('downPayment', sanitizedValue)
      },
      offerPriceInput: {
        userModified: 'OFFER_PRICE_INPUT_CHANGED',
        elementText: 'Offer Price',
        action: () => {
          setLocalPrice(sanitizedValue);
          setOfferPrice(sanitizedValue);
          setShowOfferPrice(true);
          setFinanceDownPayment(calculateDownPayment(sanitizedValue, {
            downPayment: financeDownPaymentPercentageValue === null ? financeDownPayment : null,
            downPaymentPercent: financeDownPaymentPercentageValue
          }));
          return updatePaymentPencils({
            offerPrice: sanitizedValue
          });
        }
      }
    };
    if (inputMap[name]) {
      handleNDSClick(e, {
        userModified: inputMap[name].userModified,
        elementText: inputMap[name].elementText,
        inputName: name,
        inputValue: value
      });
      setPreviousCompletedInput(name);
      return inputMap[name].action();
    }

    // Default case
    handleNDSClick(e);
    return null;
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [financeDownPaymentPercentageValue, financeDownPayment]);
  const handleOnBlur = useCallback(event => {
    const {
      name
    } = event.target;
    let cmp = '';
    let eventResult = '';
    switch (name) {
      case 'tradeInInput':
        cmp = 'estm_trd_val';
        eventResult = 'trade-in';
        break;
      case 'amountOwedInput':
        cmp = 'amt_owd_trd';
        eventResult = 'amount-owned';
        break;
      case 'downPaymentInput':
        cmp = 'dwn_pymt';
        eventResult = 'down-payment';
        break;
      case 'offerPriceInput':
        cmp = 'my_ofr';
        eventResult = 'my-offer';
        break;
      case 'dueAtSigning':
        cmp = 'due_sgn';
        eventResult = 'due-at-signing';
        break;
    }
    if (event?.target?.value) {
      handleNDSPencilling(event, {
        cmp,
        eventResult,
        feature: ndsPencilingFeature,
        inputName: name,
        inputValue: event?.target?.value
      });
    }
  }, [ndsPencilingFeature, handleNDSPencilling]);
  const handleSupportingLinkClick = value => setIsSupportingLinkClicked(value);
  const handleTradeInLinkClick = useCallback(event => {
    sendClick(ndsKBBTradeInCTAClick, event, {
      listingId,
      paymentsDuck,
      activeTab,
      financePayment: financeOffersObj?.payment,
      leasePayment: leaseOffersObj?.payment
    });
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [handleNDSClick, listingId, sendClick, updateMyWallet]);
  const handlePayoffQuoteLinkClick = useCallback(event => {
    handleNDSClick(event, {
      userModified: 'PAYOFF_QUOTE_UPDATED'
    });
    updateMyWallet({
      displayModal: true,
      hideBackButton: true,
      selectedPanelName: 'payoffQuote'
    });
    sendClick(ndsPayoffQuoteCTAClick, event, {
      listingId
    });
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [listingId]);
  const verifyCreditRatingInternal = useCallback(event => {
    handleSupportingLinkClick(true);
    handleNDSClick(event, {
      userModified: 'CREDIT_VERIFICATION_CLICKED'
    });
    updateMyWallet({
      displayModal: true,
      selectedPanelName: 'creditVerification'
    });
    sendClick(ndsVerifyCreditCTAClick, event, {
      listingId
    });
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [listingId]);
  const {
    verifyCreditRatingViaEvent
  } = useExternalSelectedCriteriaUpdate(updateUserPaymentInfo, financeOffersObj, selectedLoanTerm, loanTerm, selectedCreditTier, financeDownPayment, offerPrice, price);
  const handleCreditVerificationLinkClick = event => {
    if (enableCreditVerification) {
      verifyCreditRatingViaEvent(event, 'credit-verification-cta');
    } else {
      verifyCreditRatingInternal(event);
    }
  };
  const kbbLink = () => /*#__PURE__*/jsx(KBBTradeInCTA, {
    enableTradeIn: enableTradeIn,
    onClick: handleTradeInLinkClick,
    source: source,
    handleNDSClick: handleNDSClick,
    onFetchPersonalizedPaymentDetails: onFetchPersonalizedPaymentDetails,
    updateMyWallet: updateMyWallet
  });
  const payoffQuoteLink = () => {
    if (!(enableTradeIn && enablePayoffQuote)) {
      return null;
    }
    return /*#__PURE__*/jsx("div", {
      className: "margin-bottom-4",
      children: /*#__PURE__*/jsxs(Button, {
        onClick: event => handlePayoffQuoteLinkClick(event),
        bsStyle: "text",
        children: [amountOwed ? 'Update' : 'Get', " My Payoff Quote"]
      })
    });
  };
  const creditVerificationLink = /*#__PURE__*/jsx("div", {
    className: "margin-top-3",
    children: /*#__PURE__*/jsx(Button, {
      onClick: event => handleCreditVerificationLinkClick(event),
      bsStyle: "text",
      children: "Verify My Credit"
    })
  });
  const creditVerifiedIndicator = /*#__PURE__*/jsx("div", {
    className: "row",
    children: /*#__PURE__*/jsxs(Text, {
      componentClass: "div",
      color: "success",
      className: "display-flex gap-2 col-xs-12 col-lg-5 margin-top-3",
      children: [/*#__PURE__*/jsx(Glyphicon, {
        glyph: "success"
      }), /*#__PURE__*/jsx(Text, {
        size: 200,
        children: "Credit Verified"
      })]
    })
  });

  // Handler for continue callback from Lending Options Modal
  const handleLendingOptionsSelection = function () {
    let data = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    let event = arguments.length > 1 ? arguments[1] : undefined;
    const {
      name = '',
      rate,
      payment
    } = data;
    const newFinanceObj = {
      ...financeOffersObj,
      payment: Number.parseFloat(payment),
      lender: name,
      interestRate: Number.parseFloat(rate),
      lendingOptionSelected: true // update the lendingOptionSelected flag when selected from LendingOptionsModal
    };
    switch (name) {
      case 'Custom Rate':
        updatePaymentsInfo({
          ...paymentsInfo,
          interactions: {
            ...paymentsInfo.interactions,
            hasUpdatedNDSCalc: true,
            paymentEmphasis: true,
            fromNds: true
          },
          interestRate: {
            ...paymentsInfo?.interestRate,
            custom: Number.parseFloat(rate)
          }
        });
        break;
      case DEFAULT_RATE_LABEL:
        if (paymentsInfo?.interestRate?.custom) {
          updatePaymentsInfo({
            ...paymentsInfo,
            interestRate: {
              ...paymentsInfo?.interestRate,
              custom: null
            },
            interactions: {
              ...paymentsInfo.interactions,
              fromNds: true
            }
          });
        }
        break;
      default:
        setFinanceOffersObj(newFinanceObj);
        // emit event with selected finance options
        updateUDSPaymentObject(newFinanceObj, leaseOffersObj, cashOffersObj);
        updatePaymentsInfo({
          ...paymentsInfo,
          interestRate: {
            ...paymentsInfo.interestRate,
            custom: null
          },
          interactions: {
            ...paymentsInfo.interactions,
            fromNds: true
          }
        });
        updatePaymentPencils({
          customLender: {
            payment,
            lender: name,
            rate
          }
        });
    }
    handleNDSPencilling(event, {
      cmp: 'lend_opts_cnt'
    });
  };
  const inputProps = useMemo(() => ({
    onChange: handleInputChange,
    onBlur: handleOnBlur
  }), [handleInputChange, handleOnBlur]);
  const walletPenciledAnalyticsHandler = useCallback(() => {
    if (!isPenciledVdpListing) {
      sendClick(myWalletInteractionClick, null, {
        clickType,
        paymentsDuck
      });
      updatePenciledVdpListings(listingId);
      updatePenciledLatestPageId('vdp');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPenciledVdpListing]);
  const enableMyWallet = () => {
    walletPenciledAnalyticsHandler();
    if (!paymentEmphasis) {
      sendClick(myWalletInteractionClick, null, {
        clickType,
        paymentsDuck
      });
      updateUserPaymentInfo();
    }
  };
  const handleEmailCTAClick = context => {
    handleCreateWalletId();
    enableMyWallet();
    onEmailCTAClick(context);
  };
  const handleOnOpeningSDPFailed = context => {
    handleCreateWalletId();
    if (!paymentEmphasis) {
      updateUserPaymentInfo();
    }
    onEmailCTAClick(context);
  };
  const onNdsInitialClick = event => {
    const {
      target = ''
    } = event || {};
    const tabOptions = ['Finance', 'Lease', 'Cash'];
    const initialTabClick = tabOptions.find(tab => target?.textContent?.includes(tab)) || '';
    if (hasFiredUDSInitialClickTagging?.current === false) {
      sendClick(ndsInitialClick, null, {
        paymentsInfo,
        activeTab: initialTabClick || activeTab
      });
      hasFiredUDSInitialClickTagging.current = true;
    }
  };

  // Used by CallsToAction component and prequal link
  const digitalRetailProps = {
    buttonText: digitalRetailCTALabel,
    listedPrice: localPrice,
    listing,
    paymentsInfo: {
      ...paymentsInfo,
      downPayment: financeDownPayment,
      creditType: selectedCreditTier,
      dealType: isLeaseDeal && !leasePayment ? 'finance' : activeTab ?? initialDealType,
      dueAtSigning: dueAtSigningValue,
      activeTab,
      financePayment: financeOffersObj?.payment,
      leasePayment: leaseOffersObj?.payment
    },
    onError: () => handleOnOpeningSDPFailed('calculatorOfferSummary'),
    paymentsDuck,
    hideSendOfferCTA,
    parent: 'nds',
    dealerId,
    // mmd ID
    userDuck,
    taxesAndFeesAvailability: !isNationalRates,
    onClick: onNdsInitialClick
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchPaymentDetails = (withTaxesAndFees, applyResults, userZip) => {
    if (enableExternalPaymentFetch && onFetchPersonalizedPaymentDetails) {
      const pencilingData = {
        dealType: activeTab || initialDealType,
        downPayment: financeDownPayment,
        creditTier: obfuscatedCredit,
        leaseTerm: selectedLeaseTerm,
        loanTerm: selectedLoanTerm,
        tradeInValue: localTradeInValue,
        amountOwed: localAmountOwed,
        userZip,
        mileage: selectedMileage
      };
      onFetchPersonalizedPaymentDetails(pencilingData);
    } else if (paymentsEnabled) {
      fetchPersonalizedPayment(withTaxesAndFees, applyResults, userZip);
    }
  };
  const renderTaxesAndFees = function (tabName) {
    let uiContext = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'withRedesign';
    return /*#__PURE__*/jsx(TaxesAndFees, {
      uiContext: uiContext,
      isEstimate: digitalRetailingType === 'default',
      enableTaxesAndFees: enableTaxesAndFees,
      paymentErrorState: paymentErrorState,
      tabName: tabName,
      includeTaxesAndFees: includeTaxesAndFees,
      isNationalRates: isNationalRates,
      fetchPaymentDetails: fetchPaymentDetails,
      handleNDSPencilling: handleNDSPencilling,
      zip: zip,
      container: container,
      taxesAndFeesObj: taxesAndFeesObj,
      updatePaymentsInfo: updatePaymentsInfo,
      updatePaymentPencils: updatePaymentPencils,
      digitalRetailingType: digitalRetailingType,
      handleNDSClick: handleNDSClick,
      totalTaxesFees: totalTax,
      setHasTaxesFeesModalOpen: setHasTaxesFeesModalOpen,
      activeTab: activeTab,
      ndsPencilingFeature: ndsPencilingFeature,
      brand: brand,
      setIncludeTaxesAndFees: setIncludeTaxesAndFees,
      setPaymentCalculationDetails: setPaymentCalculationDetails,
      paymentCalculationDetails: paymentCalculationDetails,
      updateUserPaymentInfo: updateUserPaymentInfo,
      onNdsInitialClick: onNdsInitialClick,
      isUds: true,
      isLoading: isLoading || isLoadingTaxes,
      setIsLoadingTaxes: setIsLoadingTaxes,
      hasRequestedTaxesFees: hasRequestedTaxesFees,
      hasTaxesFetchError: hasTaxesFetchError,
      fetchIncentivesDetails: isIncentivesOfferEnabled ? fetchIncentivesDetails : undefined
    });
  };
  const getPriceBreakdown = tabName => {
    const fallbackPricingDetails = {
      msrp: fallbackMsrp,
      salePrice: localPrice
    };
    const _fallbackFinancePricingDetails = !_isEmpty(financePricingDetails) ? {
      ...financePricingDetails
    } : fallbackPricingDetails;
    const _priceBreakdown = {
      cash: !_isEmpty(cashPricingDetails) ? {
        ...cashPricingDetails
      } : fallbackPricingDetails,
      lease: !_isEmpty(leasePricingDetails) ? {
        ...leasePricingDetails
      } : fallbackPricingDetails
    }[tabName] || _fallbackFinancePricingDetails;
    _priceBreakdown.manufacturerOffer = _priceBreakdown.rebate;
    _priceBreakdown.offersUpgrades = _priceBreakdown.specialOfferValue;
    return _priceBreakdown;
  };

  // To be passed to OfferColumn
  const ctas = /*#__PURE__*/jsx(CallsToAction, {
    activeTab: activeTab ?? initialDealType,
    customCallToAction: customCallToAction,
    saveDealXgFetcher: saveDealXgFetcher // For esntial deal
    ,

    env: env // For saveDeaXgFetcher
    ,

    digitalRetailProps: digitalRetailProps,
    handleEmailCTAClick: handleEmailCTAClick,
    isDrEnabled: isDRAccelerate,
    listing: listing // For esntial deal
    ,

    listingId: listingId,
    vin: vin,
    nationalRates: nationalRates // For esntial deal
    ,

    paymentsDuck: paymentsDuck,
    dealsDuck: dealsDuck,
    paymentErrorState: paymentErrorState,
    zip: zip,
    hideSendOfferCTA: hideSendOfferCTA,
    creditVerificationHandler: verifyCreditRatingViaEvent
  }, "callsToAction");
  const sharedTabProps = {
    ...rest,
    handleNDSClick,
    formattedTotalPrice,
    inputProps,
    isLoading,
    onSubmit,
    renderTaxesAndFees,
    tradeInfoInputProps: {
      digitalRetailingType,
      supportingLinks: {
        kbbLink,
        payoffQuoteLink
      },
      valueOwed: localAmountOwed,
      valueTradeIn: localTradeInValue
    },
    previousCompletedInput
  };
  const disclaimerProps = useMemo(() => {
    const {
      dueInAdvance = {},
      capCost = {}
    } = leaseOffersObj;
    const dealerFees = taxesAndFeesObj[activeTab]?.dealerFees || 0;
    const lenderFees = taxesAndFeesObj[activeTab]?.lenderFees || 0;
    return {
      finance: {
        isPennyPerfectPayment,
        totalFinancing,
        localInterestRate,
        loanTerm: selectedLoanTerm,
        downPayment: financeDownPayment,
        totalInterest: financeOffersObj?.totalInterest,
        tax: formattedTotalTax,
        lender: financeOffersObj?.lender,
        salePrice: localPrice,
        payment: financeOffersObj?.payment,
        showSpecialAPR: financeOffersObj?.showSpecialAPR,
        dealerFees,
        lenderFees
      },
      cash: {
        cash: totalCashOut,
        tax: formattedTotalTax,
        tradeEquity: totalEquity,
        dealerFees,
        lenderFees
      },
      lease: {
        adjustedCapCost: capCost?.adjustedCapCost,
        acquisitionFee: leaseOffersObj?.acquisitionFee,
        capReduction: dueInAdvance?.capReduction,
        dealerName: owner?.name ?? ownerName,
        dueAtSigningValue,
        excessMileageCharge: leaseOffersObj?.excessMileageCharge,
        grossCapCost: capCost?.grossCapCost,
        leasePayment: leaseOffersObj?.payment,
        make,
        programEndDate: leaseOffersObj?.programEndDate || '',
        totalOfPayments: leaseOffersObj?.totalOfPayments,
        securityDeposit: dueInAdvance?.securityDeposit,
        selectedCreditTier,
        selectedLeaseTerm,
        selectedMileage,
        salePrice: localPrice,
        lender: leaseOffersObj?.offeredBy,
        dealerFees,
        lenderFees
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehiclePaymentDetails, localPrice, totalFinancing, formattedTotalTax, localInterestRate, selectedLoanTerm, financeDownPayment, financeOffersObj, leaseOffersObj, cashOffersObj, listingId, selectedCreditTier, selectedLeaseTerm, selectedMileage, totalCashOut, totalEquity, owner]);
  // price breakdown inside Offer Breakdown section
  const getPricingBreakdown = tabName => {
    const pricingBreakdown = {
      ...getPriceBreakdown(tabName)
    };
    if (pricingBreakdown.salePrice) {
      pricingBreakdown.listingPrice = pricingBreakdown.salePrice;
    }
    if (offerPrice) {
      pricingBreakdown.offerPrice = offerPrice;
    }
    pricingBreakdown.manufacturerOffer = pricingBreakdown.rebate;
    pricingBreakdown.offersUpgrades = pricingBreakdown.specialOfferValue;
    return pricingBreakdown;
  };
  const handleOfferBreakdownOpen = useCallback(event => {
    sendClick(ndsOfferBreakDownClick, event, {
      tabName: activeTab,
      listingId,
      walletData: {
        paymentsInfo,
        price: fallbackListingPrice,
        offerPrice
      },
      paymentsDuck
    });
  }, [activeTab, listingId, paymentsInfo, offerPrice, paymentsDuck, fallbackListingPrice]);
  const handleOfferBreakdownCTA = useCallback(event => {
    startTransition(() => {
      setShowOfferBreakdownModal(true);
    });
    fetchPaymentDetails(true, false, zip);
    if (isIncentivesOfferEnabled) fetchIncentivesDetails();
    handleOfferBreakdownOpen(event);
  }, [fetchPaymentDetails, zip, isIncentivesOfferEnabled, fetchIncentivesDetails, handleOfferBreakdownOpen]);

  // Handler to trigger Lending Options Modal open
  const handleLendingOptionsCTA = event => {
    startTransition(() => {
      setShowLendingOptionsModal(true);
    });
    sendClick(ndsLendingOptionsCTAClick, event, {
      listingId,
      enableCreditApp
    });
  };
  const offerBreakdownProps = {
    amountOwed,
    dueAtSigningValue,
    getPricingBreakdown,
    paymentsInfo,
    tradeInValue,
    diaTax: diaTaxes,
    diaFee: (diaDealerFees ?? 0) + (diaGovFees ?? 0) + (diaLenderFees ?? 0),
    firstPayment,
    cashDueFromCustomer,
    adjustedCapCost,
    isEstimate: digitalRetailingType === 'default',
    capitalizedTaxesAndFees,
    handleOfferBreakdownOpen
  };
  const leaseFinancingProps = {
    creditApp,
    digitalRetailProps,
    enableCreditApp,
    enableMyWallet,
    handleEmailCTAClick,
    hideSendOfferCTA,
    isDRAccelerate,
    isOwnerFinancingSubmitted: !_isEmpty(creditAppDeals),
    listingId,
    onFinancingCTAClick,
    dealType: activeTab,
    paymentErrorState,
    ...(offerBreakdownProps && {
      handleOfferBreakdownCTA
    })
  };
  const financingProps = {
    ...leaseFinancingProps,
    ...(showLendingOptions && {
      handleLendingOptionsCTA
    })
  };
  const fallBackNationalInterestRate = {
    name: DEFAULT_RATE_LABEL,
    rate: selectedInterestRate,
    payment: calculatePersonalPayment(localPrice, {
      loanTerm: selectedLoanTerm,
      downPayment: financeDownPayment,
      totalEquity
    }, selectedInterestRate)
  };
  useLendingOptions(paymentCalculationDetails, listingPaymentDetails, obfuscatedCredit, selectedLoanTerm, vehicleCondition, setLendingOptions);
  const getCreditVerificationLink = () => {
    if (creditVerified) {
      return enableCreditVerification ? creditVerifiedIndicator : undefined;
    }
    return creditVerificationLink;
  };
  const financeTab = /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx(FinanceTab, {
      creditTierOptions: creditTierOptions,
      creditVerificationLink: getCreditVerificationLink(),
      customDefaultFinanceTerm: customDefaultFinanceTerm,
      financeDownPayment: financeDownPayment,
      handleSelectChange: handleSelectChange,
      isSupportingLinkClicked: isSupportingLinkClicked,
      lendingOptions: lendingOptions,
      onSupportingLinkClick: handleSupportingLinkClick,
      selectedCreditTier: selectedCreditTier,
      selectedLoanTerm: selectedLoanTerm,
      showOfferPrice: showOfferPrice,
      ...sharedTabProps
    }), additionalContent]
  });
  const leaseTab = /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx(LeaseTab, {
      creditTierOptions: creditTierOptions,
      dealType: activeTab,
      dueAtSigningValue: dueAtSigningValue,
      handleSelectChange: handleSelectChange,
      leaseDownPayment: leaseDownPayment,
      leaseMileageOptions: leaseMileageOptions,
      leaseOffersObj: leaseOffersObj,
      leaseTermOptions: leaseTermOptions,
      selectedCreditTier: selectedCreditTier,
      selectedLeaseTerm: selectedLeaseTerm,
      selectedMileage: selectedMileage,
      ...sharedTabProps
    }), additionalContent]
  });
  const cashTab = /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx(CashTab, {
      showOfferPrice: showOfferPrice,
      ...sharedTabProps
    }), additionalContent]
  });
  const getFinancePayment = () => {
    if (!useCustomRate && !!financeOffersObj.payment) {
      return financeOffersObj.payment;
    }
    return fallBackFinanceObj?.payment;
  };
  const formatPayment = (errorState, payment) => formatCurrency(errorState || !_isNumber(payment) ? '---' : Math.floor(payment));
  const renderTabHeaderContent = tabName => {
    const titles = {
      finance: 'Finance',
      lease: 'Lease',
      cash: 'Cash'
    };
    const dealValues = {
      finance: formatPayment(isFinanceErrorState, getFinancePayment()),
      lease: formatPayment(isLeaseErrorState, leaseOffersObj.payment),
      cash: formatPayment(!localPrice, Math.max(0, totalCashOut))
    };
    return /*#__PURE__*/jsxs(Fragment, {
      children: [/*#__PURE__*/jsx(Text, {
        color: "subdued-lighter",
        children: titles[tabName]
      }), /*#__PURE__*/jsx("div", {
        className: "margin-top-1",
        children: isLoading ? /*#__PURE__*/jsx(LoadingSpinner, {
          height: 24,
          className: "display-flex align-items-center margin-0 margin-horizontal-auto"
        }) : /*#__PURE__*/jsxs(Fragment, {
          children: [/*#__PURE__*/jsx(Text, {
            size: 300,
            weight: "bold",
            children: dealValues[tabName]
          }), tabName !== 'cash' && /*#__PURE__*/jsx(Text, {
            size: 200,
            children: "/mo."
          })]
        })
      })]
    });
  };
  const renderIncentiveIcon = tabName => {
    const tabIncentiveMap = {
      finance: financeOffersObj?.hasOffersOrIncentives,
      cash: cashOffersObj?.hasOffersOrIncentives,
      lease: leaseOffersObj?.hasOffersOrIncentives
    };
    const incentiveIcon = /*#__PURE__*/jsx("svg", {
      width: "11",
      height: "11",
      viewBox: "0 0 11 11",
      children: /*#__PURE__*/jsx("path", {
        d: "M10.8437 6.2457L4.98877 0.370508C4.90203 0.283789 4.72855 0.21875 4.62012 0.21875H1.25897L0.5 0.869141V4.33789C0.5 4.46797 0.565055 4.61973 0.651794 4.70645L6.50671 10.56C6.59345 10.6467 6.72356 10.6467 6.81029 10.56L10.822 6.5709C10.9304 6.48418 10.9304 6.33242 10.8437 6.2457ZM2.01794 2.99375C1.67098 2.64687 1.67098 2.10488 2.01794 1.75801C2.3649 1.41113 2.90702 1.41113 3.25398 1.75801C3.60094 2.10488 3.60094 2.64687 3.25398 2.99375C2.9287 3.31895 2.3649 3.31895 2.01794 2.99375Z",
        fill: "white"
      })
    });
    return tabIncentiveMap[tabName] ? incentiveIcon : null;
  };
  const renderTabs = () => {
    const tabContentMap = {
      finance: financeTab,
      lease: leaseTab,
      cash: cashTab
    };
    if (supportedDealTypes.length === 1) {
      return tabContentMap[supportedDealTypes[0]];
    }
    const activeTabKey = activeTab && supportedDealTypes ? supportedDealTypes?.indexOf(activeTab) : 0;
    const selectableTabsContent = supportedDealTypes.map((tabName, index) => TAB_LABELS[tabName] && /*#__PURE__*/jsx(SelectableTabPanel, {
      icon: renderIncentiveIcon(tabName),
      tabHeaderContent: renderTabHeaderContent(tabName),
      inputProps: {
        id: tabName,
        name: 'estimatedPaymentTabs',
        // Should be Removed once we address - US1553680: UDS SelectedTab Dealtype Radio button Not Updating the Checked attribute on the DOM
        ...(activeTabKey === index && {
          'data-cmp': 'activeTab'
        })
      },
      children: tabContentMap[tabName]
    }, `${tabName}-${index}`));
    return /*#__PURE__*/jsx(SelectableTabs, {
      grouped: true,
      defaultActiveKey: activeTabKey,
      onSelect: onTabSelect,
      children: selectableTabsContent
    });
  };
  const renderWalletSyncTitle = /*#__PURE__*/jsx(UDSTitleContainer, {
    isPsxListing: isPrivateSeller,
    paymentsDuck: paymentsDuck,
    updateMyWallet: updateMyWallet
  });
  return (/*#__PURE__*/
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    jsxs("div", {
      "data-cmp": "ndsTabs",
      onClick: onNdsInitialClick,
      children: [/*#__PURE__*/jsxs(Form, {
        onValidSubmit: onSubmit,
        className: clsx({
          row: !isSingleColumn && !isXs,
          'padding-left-3': isSingleColumn && !isXs && showNewPriceSection,
          'padding-horizontal-3': isSingleColumn && !isXs && !showNewPriceSection,
          'padding-5': !isSingleColumn && !showWalletSyncBanner,
          'margin-top-4 margin-top-sm-6': !isSingleColumn && showWalletSyncBanner
        }),
        children: [/*#__PURE__*/jsx("div", {
          className: showNewPriceSection ? 'col-xs-12' : clsx({
            'text-left margin-top-1': isSingleColumn && isXs
          }, 'col-xs-12'),
          children: !showNewPriceSection && renderWalletSyncTitle
        }), /*#__PURE__*/jsxs("div", {
          className: clsx({
            'col-xs-12 col-sm-6': !isSingleColumn && !showNewPriceSection
          }),
          children: [renderTabs(), showCustomDisclaimerText && /*#__PURE__*/jsx(CustomDisclaimerText, {
            className: "padding-top-2 padding-bottom-4"
          })]
        }), /*#__PURE__*/jsxs("div", {
          className: clsx({
            'col-xs-12 col-sm-6 padding-left-sm-3': !isSingleColumn && !showNewPriceSection
          }),
          children: [/*#__PURE__*/jsx(OfferColumn, {
            lendingOptions: lendingOptions?.length ? lendingOptions : [fallBackNationalInterestRate],
            container: container,
            dealerFinancingCTA: dealerFinancingCTA,
            disclaimerProps: disclaimerProps,
            dueAtSigningValue: dueAtSigningValue,
            financingCallToAction: /*#__PURE__*/jsx(FinancingCallToAction, {
              ...(isFinanceDeal ? financingProps : leaseFinancingProps),
              udsCTAs: !paymentErrorState ? ctas : undefined
            }),
            handleFallbackPayment: handleFallbackPayment,
            handleLendingOptionsSelection: handleLendingOptionsSelection,
            isLoading: isLoading || isLoadingTaxes,
            isPennyPerfectPayment: isPennyPerfectPayment,
            listing: listing,
            listingPaymentDetails: listingPaymentDetails,
            offerBreakdownProps: offerBreakdownProps,
            paymentCalculationDetails: paymentCalculationDetails,
            renderTaxesAndFees: renderTaxesAndFees,
            setShowLendingOptionsModal: setShowLendingOptionsModal,
            showLendingOptionsModal: showLendingOptionsModal,
            setShowOfferBreakdownModal: setShowOfferBreakdownModal,
            showOfferBreakdownModal: showOfferBreakdownModal,
            hasTaxesFeesModalOpen: hasTaxesFeesModalOpen,
            totalCashOut: totalCashOut,
            totalEquity: totalEquity,
            totalFinancing: totalFinancing,
            totalTax: totalTax,
            paymentErrorState: paymentErrorState,
            hasTaxesFetchError: hasTaxesFetchError,
            incentivesProps: incentivesProps,
            udsLocalStateValues: udsLocalStateValues,
            priceLabel: priceLabel,
            priceDescription: priceDescription
          }), (enableNewPriceSectionOnVdp || isPrivateSeller) && /*#__PURE__*/jsx(Disclaimer, {
            container: container,
            disclaimerPaymentProps: disclaimerProps[activeTab],
            type: activeTab,
            "data-cmp": "disclaimer",
            className: clsx({
              'margin-bottom-3': isSingleColumn,
              'margin-bottom-5': !isSingleColumn && showWalletSyncBanner
            })
          })]
        }), !paymentErrorState && !enableNewPriceSectionOnVdp && !isPrivateSeller && /*#__PURE__*/jsx(Disclaimer, {
          container: container,
          disclaimerPaymentProps: disclaimerProps[activeTab],
          type: activeTab,
          "data-cmp": "disclaimer",
          className: clsx({
            'padding-3': isSingleColumn,
            'col-xs-12': !isSingleColumn && !isXs,
            'margin-top-4 margin-bottom-5': !isSingleColumn && showWalletSyncBanner
          })
        })]
      }), enableBOACreditAppForm && /*#__PURE__*/jsx(BOACreditAppForm, {
        formData: boaFormData,
        ref: boaFormRef
      })]
    })
  );
}
var NativeDealCalculator$1 = /* #__PURE__*/memo(NativeDealCalculator);
export { NativeDealCalculator$1 as default };
